import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Wallet } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import OptimizedImage from './OptimizedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cache de produtos pré-carregados
const prefetchCache = new Set();

export const ProductCard = ({ product, isFavorited: initialFavorited = false, onFavoriteChange, priority = false }) => {
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [showCheck, setShowCheck] = useState(false);
  const [showBurst, setShowBurst] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const cardRef = useRef(null);
  const imageContainerRef = useRef(null);

  // Sync prop with state when favorites are fetched after mount
  useEffect(() => {
    setIsFavorited(initialFavorited);
  }, [initialFavorited]);
  
  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;

  // Vendas simuladas baseado no estoque (quanto menor o estoque, mais vendeu)
  const soldCount = product.sold_count || (product.stock ? Math.max(30, 120 - product.stock * 2) : 0);

  // Estoque baixo - só mostra se realmente for baixo
  const isLowStock = product.stock > 0 && product.stock <= 3;

  // Função para mudar a imagem
  const changeImage = (direction) => {
    if (product.images?.length > 1) {
      if (direction > 0) {
        setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
      } else {
        setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
      }
    }
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    const endX = e.changedTouches[0].clientX;
    const diff = startX - endX;
    
    if (Math.abs(diff) > 50) {
      changeImage(diff > 0 ? 1 : -1);
    }
    setIsDragging(false);
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setStartX(e.clientX);
    setIsDragging(true);
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    const diff = startX - e.clientX;
    
    if (Math.abs(diff) > 50) {
      e.preventDefault();
      e.stopPropagation();
      changeImage(diff > 0 ? 1 : -1);
    }
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Prevenir navegação quando arrastando
  const handleClick = (e) => {
    if (Math.abs(startX - e.clientX) > 10) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    setIsFavorited(initialFavorited);
  }, [initialFavorited]);

  // Prefetch do produto quando passar o mouse (carrega antes de clicar)
  const handleMouseEnter = useCallback(() => {
    if (prefetchCache.has(product.product_id)) return;
    prefetchCache.add(product.product_id);
    
    // Pré-carregar dados do produto em background
    axios.get(`${API}/products/${product.product_id}`).catch(() => {});
    
    // Pré-carregar imagem principal em alta qualidade
    if (product.images?.[0]) {
      const img = new Image();
      img.src = product.images[0];
    }
  }, [product.product_id, product.images]);

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      alert('Faça login para favoritar produtos');
      return;
    }
    
    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${product.product_id}`, { withCredentials: true });
        setIsFavorited(false);
      } else {
        await axios.post(`${API}/favorites/${product.product_id}`, {}, { withCredentials: true });
        setIsFavorited(true);
      }
      onFavoriteChange?.();
    } catch (e) {
      console.error('Erro ao favoritar:', e);
    }
  };

  const isOutOfStock = product.out_of_stock === true;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isOutOfStock) return;
    
    // Mostrar feedback visual
    setShowCheck(true);
    setShowBurst(true);
    
    // Adicionar ao carrinho com tamanho e cor padrão
    addToCart(product, product.sizes?.[0] || 'M', product.colors?.[0] || 'Padrão', 1);
    
    // Voltar ao normal
    setTimeout(() => setShowCheck(false), 400);
    setTimeout(() => setShowBurst(false), 600);
  };

  return (
    <>
      <Link 
        ref={cardRef}
        to={`/produto/${product.product_id}`}
        className={`product-card group block bg-[#F7F7F7] overflow-hidden ${isOutOfStock ? 'opacity-50' : ''}`}
        data-testid={`product-card-${product.product_id}`}
        onMouseEnter={handleMouseEnter}
        onClick={handleClick}
        draggable={false}
      >
        {/* Image container - fundo #F7F7F7 */}
        <div 
          ref={imageContainerRef}
          className="relative aspect-[2/3] overflow-hidden bg-[#F7F7F7] cursor-grab active:cursor-grabbing select-none"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          draggable={false}
        >
          <OptimizedImage
            src={product.images[currentImageIndex] || product.images[0]}
            alt={product.name}
            className={`w-full h-full transition-all duration-300 group-hover:scale-103 ${
              product.image_fit === 'cover' ? 'object-cover' : 'object-contain'
            }`}
            style={product.image_object_position ? { objectPosition: product.image_object_position } : undefined}
            priority={priority}
          />

          {/* Badge ESGOTADO */}
          {isOutOfStock && (
            <div className="absolute top-2 left-2 z-10">
              <span className="bg-[#1a1a1a] text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                Esgotado
              </span>
            </div>
          )}

          {/* Badge LANÇAMENTO - dentro da foto, parte inferior */}
          {!isOutOfStock && product.is_launch && (
            <div className="absolute bottom-8 left-2 z-10">
              <span className="bg-[#1a1a1a] text-white text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider" data-testid={`launch-badge-${product.product_id}`}>
                Lançamento
              </span>
            </div>
          )}

          {/* Indicadores de foto */}
          {product.images?.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {product.images.map((_, idx) => (
                <span 
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentImageIndex ? 'bg-[#2d2d2d]' : 'bg-black/20'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Badge desconto */}
          {discount > 0 && !isOutOfStock && (
            <div className="absolute top-2 left-2">
              <span 
                className="bg-[#1a1a1a] px-1.5 py-0.5 text-[9px] inline-flex items-center gap-0.5 discount-badge"
              >
                <span className="text-[#C41E3A]">{discount}%</span>
                <span className="text-white">OFF</span>
              </span>
            </div>
          )}

          {/* Estoque baixo - Sem animação, apenas informativo */}
          {isLowStock && !isOutOfStock && (
            <div className="absolute top-2 left-2 mt-7">
              <span className="bg-amber-600 text-white text-[10px] font-medium px-2 py-1">
                Últimas unidades
              </span>
            </div>
          )}

          {/* Favoritar */}
          <button 
            className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all duration-200 shadow-sm"
            onClick={handleFavorite}
            data-testid={`favorite-btn-${product.product_id}`}
          >
            <Heart 
              className={`h-4 w-4 transition-colors ${
                isFavorited 
                  ? 'text-[#C4A35A] fill-[#C4A35A]' 
                  : 'text-neutral-600'
              }`} 
            />
          </button>

          {/* Botão Carrinho - Com efeito burst */}
          {!isOutOfStock && (
          <div className="absolute bottom-8 right-2">
            {/* Partículas de burst */}
            {showBurst && (
              <div className="absolute inset-0 pointer-events-none">
                <span className="absolute w-1.5 h-1.5 bg-[#2d2d2d] rounded-full animate-burst-1" />
                <span className="absolute w-1.5 h-1.5 bg-[#2d2d2d] rounded-full animate-burst-2" />
                <span className="absolute w-1 h-1 bg-[#2d2d2d] rounded-full animate-burst-3" />
                <span className="absolute w-1 h-1 bg-[#2d2d2d] rounded-full animate-burst-4" />
                <span className="absolute w-1.5 h-1.5 bg-[#2d2d2d] rounded-full animate-burst-5" />
                <span className="absolute w-1 h-1 bg-[#2d2d2d] rounded-full animate-burst-6" />
              </div>
            )}
            <button 
              className={`relative w-8 h-8 rounded-full flex items-center justify-center shadow-sm transition-all duration-150 ease-out ${
                showCheck 
                  ? 'bg-[#2d2d2d]' 
                  : 'bg-white/90 hover:bg-white hover:shadow-md'
              }`}
              onClick={handleAddToCart}
              data-testid={`add-cart-btn-${product.product_id}`}
            >
              <ShoppingBag 
                className={`h-4 w-4 transition-colors duration-150 ${
                  showCheck ? 'text-white' : 'text-[#2d2d2d]'
                }`} 
              />
            </button>
          </div>
          )}
        </div>

        {/* Product info - Mais compacto */}
        <div className="p-3 md:p-4">
          {/* Vendidos + badge "Mais vendido na última semana" */}
          {(soldCount > 0 || product.is_top_seller_week) && (
            <div className="mb-1.5 space-y-0.5">
              {soldCount > 0 && (
                <div>
                  <span className="text-[10px] text-neutral-500">
                    {soldCount}+ vendidos
                  </span>
                </div>
              )}
              {product.is_top_seller_week && (
                <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5" data-testid="badge-top-seller-week">
                  <span className="text-[9px]">🔥</span>
                  <span className="text-[9px] font-semibold uppercase tracking-wide leading-none">
                    Mais vendido na última semana
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Name */}
          <h3 className="font-medium text-neutral-800 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-[#0a0a0a] transition-colors">
            {product.name}
          </h3>

          {/* Price */}
          <div className="flex items-baseline gap-2 flex-wrap">
            {product.original_price && (
              <span className="text-xs text-neutral-400 line-through">
                R$ {product.original_price.toFixed(2).replace('.', ',')}
              </span>
            )}
            <span className="text-[#0a0a0a] text-base font-bold">
              R$ {product.price.toFixed(2).replace('.', ',')}
            </span>
          </div>

          {/* Installments */}
          <p className="text-[10px] text-neutral-400 mt-1">
            ou 12x de R$ {((product.price * 1.2211) / 12).toFixed(2).replace('.', ',')}
          </p>
          <div className="flex items-center gap-1 mt-1.5" data-testid={`cashback-preview-${product.product_id}`}>
            <Wallet className="h-3 w-3 text-emerald-600" />
            <span className="text-[10px] font-medium text-emerald-700">
              +R$ {(product.price * 0.10).toFixed(2).replace('.', ',')} cashback
            </span>
          </div>
        </div>
      </Link>
    </>
  );
};

export default ProductCard;
